/** Review Checklist page (Phase 34) — no DB, no persistence, no approval. */
import { useState, type FormEvent } from "react";
import {
  DRY_RUN_LANGUAGES,
  REVIEW_TARGETS,
  runReviewChecklistDryRun,
  type ApiResult,
  type DryRunLanguage,
  type ReviewChecklistRequest,
  type ReviewChecklistResponse,
  type ReviewTarget,
  runWorkflowPersistedFirst,
} from "../api/client";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import { FieldGrid, SelectField, TextAreaField, TextField } from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  workflow_target: ReviewTarget;
  product_name: string;
  target_market: string;
  has_high_risk_claim: boolean;
  has_evidence_gap: boolean;
  has_unsupported_sustainability_claim: boolean;
  has_food_contact_concern: boolean;
  has_cosmetic_safety_concern: boolean;
  has_industrial_hazard_concern: boolean;
  has_supplier_uncertainty: boolean;
  target_language: DryRunLanguage;
  notes: string;
}

const EMPTY: FormState = {
  workflow_target: "other",
  product_name: "",
  target_market: "",
  has_high_risk_claim: false,
  has_evidence_gap: false,
  has_unsupported_sustainability_claim: false,
  has_food_contact_concern: false,
  has_cosmetic_safety_concern: false,
  has_industrial_hazard_concern: false,
  has_supplier_uncertainty: false,
  target_language: "en",
  notes: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<ReviewChecklistResponse> };

export function ReviewChecklistPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    target_language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage) ? locale : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.product_name.trim()) errs.product_name = t.workflowDryRunRequiredHint;
    if (!form.target_market.trim()) errs.target_market = t.workflowDryRunRequiredHint;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const body: ReviewChecklistRequest = {
      workflow_target: form.workflow_target,
      product_name: form.product_name.trim(),
      target_market: form.target_market.trim().toUpperCase(),
      has_high_risk_claim: form.has_high_risk_claim,
      has_evidence_gap: form.has_evidence_gap,
      has_unsupported_sustainability_claim: form.has_unsupported_sustainability_claim,
      has_food_contact_concern: form.has_food_contact_concern,
      has_cosmetic_safety_concern: form.has_cosmetic_safety_concern,
      has_industrial_hazard_concern: form.has_industrial_hazard_concern,
      has_supplier_uncertainty: form.has_supplier_uncertainty,
      target_language: form.target_language,
      notes: form.notes.trim() || null,
    };
    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("review-checklist", body, runReviewChecklistDryRun);
    setState({ kind: "loaded", result });
  }

  const opt = (vals: readonly string[]) => vals.map((v) => ({ value: v, label: v }));
  const yesNo = [
    { value: "no", label: t.reportReviewRequiredNo },
    { value: "yes", label: t.reportReviewRequiredYes },
  ];

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">{t.appShellNavWorkflows}</span>
        <h1 className="po-section-header__title">{t.reviewChecklistTitle}</h1>
        <p className="po-note">{t.reviewChecklistSubtitle}</p>
      </header>
      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <FieldGrid>
            <SelectField label={t.reviewChecklistFieldTarget} value={form.workflow_target}
              onChange={(v) => update("workflow_target", v as ReviewTarget)} options={opt(REVIEW_TARGETS)} />
            <TextField label={t.intakeFieldProductName} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name} onChange={(v) => update("product_name", v)} error={errors.product_name} maxLength={200} />
            <TextField label={t.labelCopyFieldTargetMarket} required requiredLabel={t.workflowDryRunRequiredHint}
              value={form.target_market} onChange={(v) => update("target_market", v.toUpperCase())} error={errors.target_market} maxLength={8} />
            <SelectField label={t.labelCopyFieldTargetLanguage} value={form.target_language}
              onChange={(v) => update("target_language", v as DryRunLanguage)} options={opt(DRY_RUN_LANGUAGES)} />
            {([
              ["has_high_risk_claim", t.reviewChecklistFieldHighRisk],
              ["has_evidence_gap", t.reviewChecklistFieldEvidenceGap],
              ["has_unsupported_sustainability_claim", t.reviewChecklistFieldSustClaim],
              ["has_food_contact_concern", t.reviewChecklistFieldFoodContact],
              ["has_cosmetic_safety_concern", t.reviewChecklistFieldCosmetic],
              ["has_industrial_hazard_concern", t.reviewChecklistFieldIndustrial],
              ["has_supplier_uncertainty", t.reviewChecklistFieldSupplier],
            ] as const).map(([key, label]) => (
              <SelectField key={key} label={label}
                value={(form as unknown as Record<string, unknown>)[key] ? "yes" : "no"}
                onChange={(v) => update(key as keyof FormState, (v === "yes") as never)}
                options={yesNo} />
            ))}
            <TextAreaField label={t.intakeFieldNotes} value={form.notes} onChange={(v) => update("notes", v)} rows={2} maxLength={1000} full />
          </FieldGrid>
          {Object.keys(errors).length > 0 && <p className="po-field__error">{t.workflowDryRunValidationFailed}</p>}
          <div className="po-row">
            <Button variant="primary" type="submit" disabled={state.kind === "submitting"}>
              {state.kind === "submitting" ? t.reviewChecklistSubmitting : t.reviewChecklistSubmit}
            </Button>
            <Button variant="secondary" type="button" onClick={() => { setForm({ ...EMPTY, target_language: form.target_language }); setErrors({}); setState({ kind: "idle" }); }}>
              {t.reviewChecklistReset}
            </Button>
          </div>
        </form>
      </section>
      {state.kind === "loaded" && (
        <DataState state={{ kind: "loaded", result: state.result }} t={t}
          renderOk={(data, requestId) => <ReviewChecklistReport data={data} requestId={requestId} t={t} />} />
      )}
    </AppLayout>
  );
}

function ReviewChecklistReport({ data, requestId, t }: { data: ReviewChecklistResponse; requestId: string | null; t: UiStrings; }): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-review-checklist-report">
      <div className="po-halt-banner po-halt-banner--medium" role="alert">{data.no_approval_notice}</div>
      <ReportSection title={t.reviewChecklistReportChecklist}><BulletList items={data.review_checklist} /></ReportSection>
      <ReportSection title={t.reviewChecklistReportCriteria}><BulletList items={data.approval_criteria} /></ReportSection>
      <ReportSection title={t.reviewChecklistReportRisks}><BulletList items={data.risk_review_items} empty={t.reportNoFlags} /></ReportSection>
      <ReportSection title={t.reviewChecklistReportEvidence}><BulletList items={data.evidence_required} empty={t.reportNoFlags} /></ReportSection>
      <ReportSection title={t.reviewChecklistReportExport}><BulletList items={data.export_readiness} /></ReportSection>
      <ReportSection title={t.reviewChecklistReportDecision}><BulletList items={data.reviewer_decision_template} /></ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
